// src/components/sepa/NewSepaForm.jsx
import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Divider,
  InputAdornment,
  LinearProgress,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import SepaFilters from "./SepaFilters";
import SepaPreviewTable from "./SepaPreviewTable";
import SepaSummaryBar from "./SepaSummaryBar";
import {
  defaultBookMonth,
  buildPain008Xml,
  buildSepaRows,
  validateIBAN,
} from "./SepaUtils";
import { useGetSitesQuery } from "../../features/sites/sitesApiSlice";
import Iconify from "../iconify";

export default function NewSepaForm({
  members = [],
  contracts = [],
  creditor = {
    creditorName: "",
    creditorIBAN: "",
    creditorBIC: "",
    creditorId: "",
    remittanceInfo: "",
  },
  onExport,
  onUpdatePayedTill, // async (memberIds: string[], payedTill: "MM/YYYY", regMemberIds?: string[]) => Promise<void>
  loading = false,
}) {
  const [bookMonth, setBookMonth] = useState(defaultBookMonth());
  const [collectionDate, setCollectionDate] = useState(
    dayjs().add(2, "day").format("YYYY-MM-DD")
  );
  const [markPaid, setMarkPaid] = useState(true);
  const [selectedSiteIds, setSelectedSiteIds] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [additionalFeePerMember, setAdditionalFeePerMember] = useState(0);
  const [remittanceNote, setRemittanceNote] = useState("");

  const remittanceTemplate = creditor?.remittanceInfo || "";

  // Load sites to power the Standort filter
  const { data: sites = [], isLoading: loadingSites } = useGetSitesQuery();

  // Build rows (one per contract) – site filtering is based on member sites
  const rows = useMemo(
    () =>
      buildSepaRows({
        members,
        contracts,
        monthLabel: bookMonth,
        remittanceTemplate,
        additionalFeePerMember,
        remittanceNote,
        selectedSiteIds,
      }),
    [
      members,
      contracts,
      bookMonth,
      remittanceTemplate,
      additionalFeePerMember,
      remittanceNote,
      selectedSiteIds,
    ]
  );

  const totalAmount = useMemo(
    () => rows.reduce((s, r) => s + r.amount, 0),
    [rows]
  );

  const filteredRows = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return rows;

    return rows.filter((r) => {
      const haystack = [
        r.debtorName,
        r.iban,
        r.remittance,
        r.mandateId,
        r.contractId,
      ]
        .filter(Boolean)
        .map((v) => String(v).toLowerCase());

      const amountText = String(r.amount ?? "").toLowerCase();
      return (
        haystack.some((value) => value.includes(term)) ||
        amountText.includes(term)
      );
    });
  }, [rows, searchTerm]);

  const canGenerate =
    rows.length > 0 &&
    creditor?.creditorName &&
    creditor?.creditorId &&
    creditor?.creditorIBAN &&
    validateIBAN(creditor.creditorIBAN) &&
    /^\d{4}-\d{2}-\d{2}$/.test(collectionDate);

  const handleGenerate = async () => {
    if (!canGenerate) return;

    const xml = buildPain008Xml({
      creditor,
      collectionDate,
      payments: rows.map((r) => ({
        debtorName: r.debtorName,
        iban: r.iban,
        bic: r.bic,
        amount: r.amount,
        mandateId: r.mandateId,
        mandateDate: r.mandateDate,
        remittance: r.remittance,
      })),
    });

    // download
    const blob = new Blob([xml], { type: "text/xml;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    const stamp = dayjs().format("YYYYMMDD-HHmmss");
    a.download = `SEPA_${stamp}.xml`;
    document.body.appendChild(a);
    a.click();
    a.remove();

    // (optional) mark as paid
    if (markPaid && typeof onUpdatePayedTill === "function") {
      const memberIds = rows.flatMap((r) => r.memberIds).filter(Boolean);
      const regMemberIds = rows
        .flatMap((r) => r.regMemberIds || [])
        .filter(Boolean);
      try {
        await onUpdatePayedTill(memberIds, bookMonth, regMemberIds);
      } catch (e) {
        console.error("Mark paid failed:", e);
      }
    }

    onExport?.(xml, {
      totalAmount,
      transactions: rows.length,
      bookMonth,
      collectionDate,
      memberIds: rows.flatMap((r) => r.memberIds).filter(Boolean),
      regMemberIds: rows.flatMap((r) => r.regMemberIds || []).filter(Boolean),
      siteIds: selectedSiteIds,
    });
  };

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="h5" sx={{ mb: 1 }}>
        SEPA-Lastschrift (Mitgliedsbeiträge)
      </Typography>
      <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
        Es werden <strong>nur aktive Mitglieder</strong> berücksichtigt. Pro
        Vertrag eine Lastschrift (Summe).
      </Typography>
      {(loading || loadingSites) && <LinearProgress sx={{ mb: 2 }} />}
      <SepaFilters
        bookMonth={bookMonth}
        onChangeBookMonth={setBookMonth}
        collectionDate={collectionDate}
        onChangeCollectionDate={setCollectionDate}
        sites={sites}
        selectedSiteIds={selectedSiteIds}
        onChangeSelectedSiteIds={setSelectedSiteIds}
      />
      <Divider sx={{ mb: 2 }} />
      <Typography variant="h6" sx={{ my: 2 }}>
        Möglichkeit zusätzlichen Betrag abzubuchen. (Verbandsgebühr o.ä.)
      </Typography>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 2 }}>
        <TextField
          size="small"
          type="number"
          label="Zusatzgebühr pro Mitglied"
          value={additionalFeePerMember}
          onChange={(e) =>
            setAdditionalFeePerMember(Math.max(0, Number(e.target.value) || 0))
          }
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">{"\u20ac"}</InputAdornment>
            ),
          }}
          sx={{ minWidth: 220 }}
        />
        <TextField
          size="small"
          label="Verwendungszweck-Zusatz"
          value={remittanceNote}
          onChange={(e) => setRemittanceNote(e.target.value)}
          placeholder="Zusätzlicher Hinweis für jede Transaktion"
          sx={{ flex: 1, minWidth: 260 }}
        />
      </Box>
      <Divider sx={{ mb: 2 }} />
      <Box sx={{ display: "flex", justifyContent: "flex-start", mb: 1 }}>
        <TextField
          size="small"
          sx={{ minWidth: 250 }}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Tabelle durchsuchen..."
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Iconify
                  icon="eva:search-fill"
                  sx={{ color: "text.disabled", width: 18, height: 18 }}
                />
              </InputAdornment>
            ),
          }}
        />
      </Box>
      <SepaPreviewTable rows={filteredRows} />
      <SepaSummaryBar
        rowsCount={rows.length}
        totalAmount={totalAmount}
        canGenerate={canGenerate}
        markPaid={markPaid}
        onToggleMarkPaid={setMarkPaid}
        onGenerate={handleGenerate}
      />
    </Paper>
  );
}

NewSepaForm.propTypes = {
  members: PropTypes.array.isRequired,
  contracts: PropTypes.array.isRequired,
  creditor: PropTypes.shape({
    creditorName: PropTypes.string.isRequired,
    creditorIBAN: PropTypes.string.isRequired,
    creditorBIC: PropTypes.string, // optional
    creditorId: PropTypes.string.isRequired,
    remittanceInfo: PropTypes.string,
  }).isRequired,
  onExport: PropTypes.func,
  onUpdatePayedTill: PropTypes.func,
  loading: PropTypes.bool,
};
